/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import ChartInsightsQueryParams from './ChartInsightsQueryParams';

/**
 * The chartInsightsQuerySpec is used to convert data into a valid querySpec for the chart-insights query.
 */
class chartInsightsQuery {
	constructor(widgetContentApi, dashboardApi) {
		this.widgetContentApi = widgetContentApi;
		this.dashboardApi = dashboardApi;
		this._useFPDAsInsightsInput = undefined;
	}

	/*For visualizations such as decision tree, Chart-Insgiths API supports using FPD call's response as input*/
	useFPDAsInsightsInput(){
		const visualization = this.widgetContentApi.getFeature('Visualization');
		const visDefinition  = visualization && visualization.getDefinition();
		if( visDefinition ) {
			this._useFPDAsInsightsInput  = visDefinition.getProperty('useFPDAsInsightsInput');
		}
		return this._useFPDAsInsightsInput;
	}


	/**
	 * execute chartInsights queries given a queryResults object (called by getInsights).
	 * @param queryResultsObject - the queryResults which could contain 1 or more DSS Results for multi-layer
	 * @returns a promise which is resolved with the insights resposes.
	 */
	execute(queryResultsObject) {
		const querySpecs = this.getChartInsightsQuerySpecs(queryResultsObject);
		return this.executeQuerySpecs(querySpecs);
	}


	/**
	 * execute chartInsights queries given a set of querySpecs (see getChartInsightsQuerySpecs)
	 * @param querySpecs - the querySpecs to call chartInsights with
	 * @returns a promise which is resolved with the insights resposes.
	 */
	//Normally, this method will run a single smarts query.
	//For maps, there can be up to 3 layers and, up to 3 queries.
	executeQuerySpecs(querySpecs=[]) {
		const promises=[];
		const smartsService = this.dashboardApi.getFeature('SmartsService');
		querySpecs.forEach(querySpec => promises.push(this._runSmartsQuery(querySpec, smartsService)));
		if (promises.length > 0) {
			return Promise.all(promises).then((results) => {
				let finalInsightsArray = [];
				results.forEach((result) => {
					if (result.data && result.data.insights) {
						finalInsightsArray = finalInsightsArray.concat(result.data.insights);
					}
				});
				return finalInsightsArray;
			});
		} else {
			return Promise.resolve([]);
		}
	}

	/**
	 * @param chartInsightsData - the results data needed to construct the chart insights query spec.
	 * @returns one or more smarts query specifications for the chart-insights query used by the execute() api.
	 */
	getChartInsightsQuerySpecs(chartInsightsData) {
		const querySpecs=[];
		if (chartInsightsData && chartInsightsData.getResult) {
			//Make a query for each layer...usually 1 (maps can have up to 3 layers)
			chartInsightsData.getQueryResultIdList().forEach((layerId) => {
				const queryResult = chartInsightsData.getResult(layerId);
				const dssQueryResultString = queryResult && queryResult.getPropertyValue('RawData.internal');
				const dssQueryResult = dssQueryResultString && JSON.parse(dssQueryResultString);
				const querySpec = this._buildQuerySpecFromUnprocessedDSSResponse(dssQueryResult, /*layerId*/layerId);
				if (querySpec) {
					querySpecs.push(querySpec);
				}
			});
		}
		return querySpecs;
	}

	/**
	 * Get a chartInsights query specification given a raw dssResponse with a single query.
	 * Currently, this path is only used for multi-measure (single response) but returns an array for possible future needs.
	 * @param dssResponse - A json representation of the response from the main dss query.
	 * @param options - options
	 * @param options.layerId - the layerId this query response is for.
	 * @returns An array with (currently) one smarts query specification for the chart-insights query used by the execute() api.
	 */
	getChartInsightsQuerySpecsFromUnprocessedDSSResponse(dssResponse, options) {
		const querySpecs=[];
		const querySpec = this._buildQuerySpecFromUnprocessedDSSResponse(dssResponse, options && options.layerId);
		if (querySpec) {
			querySpecs.push(querySpec);
		}
		return querySpecs;
	}

	/**
	 * @private
	 * Build the chart-insights query spec
	 * @param {Object} dssResponse - A json representation of the response from the main dss query.
	 * @returns The chart-insights query spec (or null if the response is empty)
	 */
	_buildQuerySpecFromUnprocessedDSSResponse(dssResponse, layerId) {
		if (dssResponse && dssResponse.data && layerId) {
			// NOTE: We should try to get DSS to add dataItemUniqueId's to nested data items, then we could simply use the response 'as-is'
			const querySpec = {
				data: {
					data: dssResponse.data,
					dataItems: this._getExecutionDataItemsDef(dssResponse.dataItems),
					hasNext: dssResponse.hasNext,
					version: dssResponse.version
				},
				params: this._getChartInsightsDictionary(layerId, dssResponse.version)
			};
			if (dssResponse.edges) {
				delete querySpec.data.dataItems;
				querySpec.data.edges = dssResponse.edges;
			}
			return querySpec;
		}
		return null;
	}

	_runSmartsQuery(querySpec, smartsService) {
		const options = {
			url: 'v1/predict/explorations/chart-insights',
			querySpec: querySpec,
			sender: 'getInsights'
		};
		return smartsService.runQuery(options).catch(() => {
			//Don't fail...just return no insights if a query fails.
			return [];
		});
	}

	/**
	 * @private
	 * @function _getExecutionDatapointsDef
	 * Build the datapoints part of the execution query
	 * @returns the datapoints part of the execution query spec
	 */
	_getExecutionDatapointsDef(data) {
		//TODO: Following annotations but...is there anything in here other than pt?
		return data && data.map((row) => {
			return {
				pt: row.pt
			};
		});
	}

	/**
	 * @private
	 * @function _getExecutionDataItemsDef
	 * Build the dataitems part of the execution query
	 * This is essentially equal to the result Data Items spec except that for nested items,
	 * the 'hierarchy' definition needs to be augmented with the uniqueId of the dataItems in the 'h'
	 * @returns the dataitems part of the execution query spec
	 */
	_getExecutionDataItemsDef(dataItems) {
		return dataItems && dataItems.map((dataItem) => {
			// Is the slot nested?
			return this._processResultDataItem(dataItem);
		});
	}

	//Process a single result data item (which could be a regular dataItem or a nested dataItem)
	//For non-nested dataItems, this is an identity function.
	//@returns a single result data item in the form needed by the chartInsights query.
	_processResultDataItem(dataItem) {
		if (dataItem.itemClass.h.length > 1) {
			return this._processNestedResultDataItem(dataItem);
		}
		return dataItem;
	}

	//Process a single nested data item.
	//For chartInsights, the hierarchy entries for each item in the stacked resultDataItem need to include
	//the uniqueId of the dataItem (the normal queryResults don't include this)
	//@returns a single nested result data item in the form needed by the chartInsights Query.
	_processNestedResultDataItem(nestedDataItem) {
		const slots = this.widgetContentApi.getFeature('Visualization').getSlots();
		if (slots) {
			return {
				itemClass:{
					h: nestedDataItem.itemClass.h.map((header) => {
						const dataItem = slots.getDataItemList().filter(dataitem => dataitem.getId() === header.id);
						return {
							// inject the uniqueId
							id: dataItem[0].getId(),
							u: header.u,
							d: header.d
						};
					}),
					id: nestedDataItem.itemClass.id
				},
				items: nestedDataItem.items
			};
		}
		return nestedDataItem;
	}

	/**
	 * @returns true if NLT is supported for this visualization/data configuration.
	 *			false for visualizations that have no data (like comet or spiral) OR any othe unsupported cases (such as multi-measure)
	 */
	chartInsightsQuerySupported() {
		const noDataQueryRequired = this.widgetContentApi.getFeature('Visualization').getDefinition().getProperty('noDataQuery');
		//Any visualization that has no data can't have nlt except those that declares use FPD request result as Insights inputs
		if (noDataQueryRequired && !this.useFPDAsInsightsInput()) {
			return false;
		}

		//There's no point in generating smartInsights for visualizations that don't have roles.
		const hasRolesDefined = this.widgetContentApi.getFeature('Visualization').getSlots().getMappedSlotList().some((slot) => {
			return slot.getDefinition().getRole();
		});
		if (!hasRolesDefined) {
			return false;
		}

		//No checks have failed....the query is supported.
		return true;
	}

	_containsOlap() {
		const visOlapQueryHelper = this.widgetContentApi.getFeature('Visualization.olapContentHelper');
		//We call an api "likelyContainsOlap" to ensure we don't add stats calculations for OLAP
		//OR for edge cases where calculations might contain OLAP in mixed packages
		//where we can't tell if the vis is OLAP or not from other items.
		return visOlapQueryHelper && visOlapQueryHelper.likelyContainsOlap();
	}

	//CALCULATIONS CASE: Calculations require each dataItem to be supplied as a calculation operand
	//					 (including stacked case)
	getChartInsightsCalculationArray(annotations = []) {
		this._buildRolesById(annotations);

		if (!this.chartInsightsQuerySupported()) {
			return annotations;
		} else if(this.useFPDAsInsightsInput() || this._containsOlap()){
			//For charts that use FPD request results does not need build Calcualtion Array
			return [];
		}

		//Make a note of the dataItems that have calculations added to the query for
		//annotations or NLT insight types.
		const calculationItemsAlreadyAdded = {};

		const chartInsightsQueryParamsByLayer={};

		const dataSourceId = this.dashboardApi.getActiveDataSourceId();
		const dataSource = this.widgetContentApi.getFeature('Visualization').getDataSource(dataSourceId);
		const dataItemSlotMappingInfo = this.widgetContentApi.getFeature('Visualization').getSlots().getMappingInfoList();

		dataItemSlotMappingInfo.forEach(dataItemSlotMapping => {
			const layerIds = dataItemSlotMapping.slot.getDefinition().getDatasetIdList();
			layerIds.forEach(layerId => {
				chartInsightsQueryParamsByLayer[layerId] = chartInsightsQueryParamsByLayer[layerId] || new ChartInsightsQueryParams(layerId);
				//If there is a role from the suggestion service, use it.  If there is a response role in the slot, we also need its calculations.
				const roleById = this.roleById[dataItemSlotMapping.dataItem.getId()] || dataItemSlotMapping.slot.getDefinition().getRole();
				const role = dataItemSlotMapping.slot.getDefinition().getRole()==='response' ? 'response' : roleById;
				if (chartInsightsQueryParamsByLayer[layerId].addParam(dataItemSlotMapping.slot, dataItemSlotMapping.dataItem, false, role, dataSource )) {
					calculationItemsAlreadyAdded[dataItemSlotMapping.dataItem.getId()]=1;
				}
			});
		});

		//Now, pass through the map, convert it to an array and make sure response is first and others are after for each entry.
		let chartInsightsCalculationArray = [];
		chartInsightsCalculationArray = chartInsightsCalculationArray.concat(annotations);
		for (const layerId in chartInsightsQueryParamsByLayer) {
			if (chartInsightsQueryParamsByLayer[layerId].hasParams()) {
				chartInsightsCalculationArray.push(chartInsightsQueryParamsByLayer[layerId].getParams());
			}
		}

		chartInsightsCalculationArray = chartInsightsCalculationArray.concat(this._addRowCountCalculationsForAverages(calculationItemsAlreadyAdded, dataItemSlotMappingInfo, dataSource));

		return chartInsightsCalculationArray;
	}

	//The compare table reports insights when the aggregation type of a measure is average.
	//Information such as the number of details that comprise the average of a chart or selection are reported in tooltips
	//These calculation results are also used to compute selection and summary weighted averages as well as combined averages.
	//This function Adds rowCount calculations to the DSS query (if not already added for use in smart annotations or NLT)
	//The Same rules and limitations are applied for adding these calculations (ie: not added for binned items or OLAP)
	_addRowCountCalculationsForAverages(calculationItemsAlreadyAdded = [], dataItemSlotMappingInfo, dataSource) {
		const additionalCalculations = [];

		dataItemSlotMappingInfo.forEach(dataItemSlotMapping => {
			const layerIds = dataItemSlotMapping.slot.getDefinition().getDatasetIdList();
			layerIds.forEach(layerId => {
				const calcParams = new ChartInsightsQueryParams(layerId);
				const slotDefType = dataItemSlotMapping.slot.getDefinition().getType();
				const dataItemType = dataItemSlotMapping.dataItem.getType();
				const dataItemAggregateType = dataItemSlotMapping.dataItem.getAggregation();
				if ((slotDefType ==='ordinal' || (dataItemType === 'fact' &&  slotDefType === 'any' )) &&
				dataItemAggregateType ==='avg' &&
					!calculationItemsAlreadyAdded[dataItemSlotMapping.dataItem.getId()]) {
					const dssRowCountCalculation = calcParams.addDSSRowCountCalculation(dataItemSlotMapping.slot, dataItemSlotMapping.dataItem, dataSource);
					if (dssRowCountCalculation) {
						additionalCalculations.push(dssRowCountCalculation);
					}
				}
			});
		});
		return additionalCalculations;
	}

	_buildRolesById(annotations) {
		this.roleById = {};
		annotations && annotations.forEach((annotation) => {
			annotation.dataItems.forEach((dataItem, index) => {
				this.roleById[dataItem]=annotation.roles[index];
			});
		});
	}

	//This helper function checks if the passed in Slot supports multimeasure series or not. The new Slot API does not have a way to do this, and LiveWiget uses a Helper class that is not exposed to us.
	_isMultiMeasureSlot(slot) {
		const dataItemList = slot.getDataItemList();
		return !!dataItemList.find(dataItem => dataItem.getColumnId() == '_multiMeasuresSeries');
	}
	//DICTIONARY CASE: 	For the dictionary, the id of the stacked item (not the dataItems that comprise it)
	//					should be shipped to the smarts query (since it matches the shape of the data)
	_getChartInsightsDictionary(layerId, version) {
		const chartInsightsQueryParams = new ChartInsightsQueryParams(layerId, version);
		const dataItemSlotMappingInfo = this.widgetContentApi.getFeature('Visualization').getSlots().getMappingInfoList();
		const dataSourceId = this.dashboardApi.getActiveDataSourceId();
		const dataSource = this.widgetContentApi.getFeature('Visualization').getDataSource(dataSourceId);
		const dataSourceMoserApi = this.dashboardApi.getFeature('DataSources.moser').getModule(dataSourceId);

		dataItemSlotMappingInfo.forEach(dataItemSlotMapping => {
			const slot = dataItemSlotMapping.slot;
			//All dataItems in the same slot have the same role. (eg: explanatory or response)
			if (slot.getDefinition().getDatasetIdList().includes(layerId) && !this._isMultiMeasureSlot(slot)) {
				chartInsightsQueryParams.addParam(dataItemSlotMapping.slot, dataItemSlotMapping.dataItem, true, /*role*/undefined, dataSource, dataSourceMoserApi);
			}
		});

		return { dictionary: chartInsightsQueryParams.getParams() };
	}

}

export default chartInsightsQuery;



// WEBPACK FOOTER //
// ./src/js/features/insights/ChartInsightsQuery.js